import {
  getLineItemsCount,
  getOrCreateCart,
  initStorefrontCart,
  toShopifyGlobalId,
  toShopifyNumericId,
} from './modules/storefront-cart';

import { CartPage } from './modules/cart/CartPage';
import { EmbarkCart } from 'shopify-buy';
import { Drawer } from './modules/cart/Drawer';
import { notifyHandledException } from './utils/errorLogger';
import { render } from 'preact';
import { trapFocus } from './utils/trapFocus';
import { syncCartWithIterable } from './modules/cart/integrations/iterable';

window.Shopify = window.Shopify || {};
if (typeof window.Shopify.formatMoney !== 'function') {
  // should be defined by Shopify for the liquid pages, this polyfill is needed for WordPress pages.
  window.Shopify.formatMoney = function (
    cents: number,
    format = '${{amount}}'
  ): string {
    return format
      .replace(/{{\s*amount_no_decimals\s*}}/, (cents / 100).toFixed(0))
      .replace(/{{\s*amount\s*}}/, (cents / 100).toFixed(2));
  };
}

const root = document.getElementById('cart-drawer-root');
if (root) {
  render(<Drawer />, root);
} else {
  // eslint-disable-next-line no-console
  console.error(
    'cannot render cart drawer, #cart-drawer-root is not present in the DOM'
  );
  notifyHandledException(
    new Error(
      'Failed to render the cart drawer, root element was not present on the page'
    )
  );
}

const cartPageRoot = document.getElementById('cart-page-root');
if (cartPageRoot) {
  render(<CartPage />, cartPageRoot);
}

initStorefrontCart();

const cartButtons = document.querySelectorAll('[data-slidecart-open]');
cartButtons.forEach((cartButton) => {
  cartButton.addEventListener('click', (e) => {
    e.preventDefault();

    window._cartDrawerOpenedBy = cartButton;

    document.dispatchEvent(new CustomEvent('cart-drawer:toggle'));
  });
});

const atcButtons = document.querySelectorAll('button[id^="AddToCart"]');
atcButtons.forEach((atcButton) => {
  atcButton.addEventListener('click', () => {
    window._cartDrawerOpenedBy = atcButton;
  });
});

// update cart count in the navigation
const cartCountElements = document.querySelectorAll('[data-cart-count]');

document.addEventListener('cart:updated', (event) => {
  const e = event as CustomEvent;
  const { cart }: { cart: EmbarkCart } = e.detail;

  cartCountElements.forEach((element) => {
    element.innerHTML = `${getLineItemsCount(cart) || 0}`;
  });

  updateOfferIfAplicable(cart).catch(notifyHandledException);
});

document.addEventListener('cart-drawer:opened', () => {
  const closeButton = document.querySelector(
    '#cart-drawer-root .cart-drawer__header__close'
  );

  if (closeButton && closeButton instanceof HTMLElement) {
    closeButton.focus();
  }

  // Restrict keyboard access to cart drawer when opened
  const cartDrawer = document.querySelector<HTMLElement>(
    '.cart-drawer[role="dialog"]'
  );
  if (!cartDrawer) return;

  cartDrawer.addEventListener('keydown', trapFocus);
});

document.addEventListener('cart-drawer:closed', (event) => {
  const openingButton = window._cartDrawerOpenedBy;

  if (openingButton && openingButton instanceof HTMLElement) {
    openingButton.focus();
  }

  const e = event as CustomEvent;
  const { cart }: { cart: EmbarkCart } = e.detail;

  cartCountElements.forEach((element) => {
    element.innerHTML = `${getLineItemsCount(cart)}`;
  });
});

document.addEventListener('cart:add-to-cart', (event) => {
  if (!(event instanceof CustomEvent)) {
    return;
  }

  const detail: {
    variantId?: string;
    quantity?: number | string;
    sellingPlanId?: string;
  } = event.detail;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'embark/add_to_cart',
    add_to_cart: {
      item: {
        id: detail.variantId,
        variant_id: toShopifyNumericId(detail.variantId),
        quantity: detail.quantity,
        selling_plan_id: detail.sellingPlanId,
      },
    },
  });
});
document.addEventListener('cart:atc:recommended', () => {
  const drawerContent = document.querySelector('.cart-drawer__content');
  if (drawerContent instanceof HTMLElement) {
    drawerContent.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }
});

function getEcommerceObject(cart: EmbarkCart) {
  return {
    currencyCode: cart.cost.totalAmount.currencyCode,
    cart: {
      actionField: {
        option: 'cart',
      },
      products: cart.lines.edges.map((line) => {
        const item = line.node;
        return {
          name: item.merchandise.product.title,
          id: item.merchandise.id?.toString().split('/').reverse().shift(),
          shopifyId: item.merchandise.id,
          price: item.merchandise.price?.amount || '0',
          brand: item.merchandise.product?.vendor || 'Embark',
          category: item.merchandise.product?.productType,
          quantity: item.quantity,
          variant: item.merchandise.title,
        };
      }),
    },
  };
}

const sendViewCartEvent = async (): Promise<void> => {
  const cart = await getOrCreateCart();

  if (!cart) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  const ecommerce = getEcommerceObject(cart);
  window.dataLayer.push({
    event: 'view_cart',
    ecommerce,
  });
};

const updateOfferIfAplicable = async (cart: EmbarkCart): Promise<void> => {
  const offerData = document.getElementById('offer-data');

  if (!offerData) {
    return;
  }

  const isOfferActive =
    offerData.getAttribute('data-is-offer-active') === 'true';
  const itemsThatQualify = JSON.parse(
    offerData.getAttribute('data-offer-qualifying-products') || '[]'
  ).map((item: string) => toShopifyGlobalId(item));
  const offerElement = document.getElementById('cart-page__offer');

  const elegibleMessage =
    offerData.getAttribute('data-offer-applies-message') || '';
  const ineligibleMessage =
    offerData.getAttribute('data-offer-does-not-apply-message') || '';

  if (!offerElement || !isOfferActive || !cart.lines || !cart.lines.edges) {
    return;
  }

  const hasItemsInCart = cart.lines.edges.some((line) => {
    const item = line.node;
    const addons = item.attributes
      .find((att) => att.key === '_addons')
      ?.value?.split(',');

    return (
      itemsThatQualify.includes(item.merchandise.id) ||
      (addons && addons.some((addon) => itemsThatQualify.includes(addon)))
    );
  });

  if (!hasItemsInCart) {
    offerElement.innerHTML = ineligibleMessage;
    offerElement.classList.add('not-eligible');
    offerElement.classList.remove('eligible');
    return;
  }

  offerElement.classList.remove('not-eligible');
  offerElement.classList.add('eligible');
  offerElement.innerHTML = elegibleMessage;
};

document.addEventListener('cart-drawer:opened', sendViewCartEvent);

syncCartWithIterable();
